import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, TextField, Grid, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip } from '@mui/material';
import { CloudUpload, GetApp, PictureAsPdf, Image, Description as DescriptionIcon, Delete, Search as SearchIcon } from '@mui/icons-material';
import { fetchProjectDocuments, uploadProjectDocument, deleteProjectDocument } from '../api/ProjectApi';
import { fetchDepartments } from '../api/DepartmentsApi'; // Ensure this path is correct
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import mammoth from 'mammoth';
import ModalImage from 'react-modal-image';
import { read, utils } from 'xlsx';
import config from '../config/config'; // Ensure this path is correct
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const ProjectDocuments = ({ projectId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [description, setDescription] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false); // Define open state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [documentContent, setDocumentContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridApi, setGridApi] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const documentsData = await fetchProjectDocuments(projectId);
        setDocuments(documentsData);
        console.log('Documents:', documentsData);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchDocuments();
    }
  }, [projectId]);

  useEffect(() => {
    const fetchDepartmentsData = async () => {
      try {
        const departmentsData = await fetchDepartments();
        setDepartments(departmentsData);
        console.log('Department Details:', departmentsData);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    fetchDepartmentsData();
  }, []);

  const sanitizeFileName = (name) => {
    return name.replace(/[^\w.-]/g, '_');
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const maxSize = 20 * 1024 * 1024; // 20MB

    if (selectedFile.size > maxSize) {
      setSnackbarMessage('File size exceeds the maximum limit of 20 MB.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (selectedFile) {
      const sanitizedFileName = sanitizeFileName(selectedFile.name);
      const sanitizedFile = new File([selectedFile], sanitizedFileName, { type: selectedFile.type });
      setFile(sanitizedFile);
      setFileName(sanitizedFileName);
    }
  };

  const handleDocumentNameChange = (event) => {
    setDocumentName(event.target.value);
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleDepartmentChange = (event) => {
    setDepartmentId(event.target.value);
  };

  const handleUpload = async () => {
    if (!file || !documentName || !description || !departmentId) {
      setSnackbarMessage('Please fill in all required fields and select a file.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setUploading(true);
    try {
      await uploadProjectDocument(projectId, file, documentName, description, departmentId);
      // Refresh the document list after upload
      const documentsData = await fetchProjectDocuments(projectId);
      setDocuments(documentsData);
      setFile(null);
      setFileName('');
      setDocumentName('');
      setDescription('');
      setDepartmentId('');
      setSnackbarMessage('Document uploaded successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error uploading document:', error);
      setSnackbarMessage('Failed to upload document. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const handleClickOpen = (document) => {
    console.log('Opening document:', document);
    setSelectedDocument(document);
    setOpen(true);
    downloadAndRenderDocument(document);
  };

  const handleClose = () => {
    console.log('Closing document viewer');
    setOpen(false);
    setSelectedDocument(null);
    setDocumentContent(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDownload = (documentId, fileName) => {
    console.log('Downloading document:', documentId, fileName);
    setDownloading(true);
    const token = localStorage.getItem('authToken');
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${config.apiBaseUrl}/api/pdocs/download/${documentId}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'blob';

    xhr.onload = function () {
      if (xhr.status === 200) {
        const url = window.URL.createObjectURL(xhr.response);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setSnackbarMessage('File downloaded successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        console.error('Failed to download file:', xhr.statusText);
        setSnackbarMessage('Failed to download file. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      setDownloading(false);
    };

    xhr.onerror = function () {
      console.error('Network error while downloading file.');
      setSnackbarMessage('Network error while downloading file.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDownloading(false);
    };

    xhr.send();
  };

  const handleDelete = async (documentId) => {
    console.log('Deleting document:', documentId);
    setDeleting(true);
    try {
      await deleteProjectDocument(documentId);
      // Refresh the document list after delete
      const documentsData = await fetchProjectDocuments(projectId);
      setDocuments(documentsData);
      setSnackbarMessage('Document deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting document:', error);
      setSnackbarMessage('Failed to delete document. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  const downloadAndRenderDocument = (document) => {
    console.log('Downloading and rendering document:', document);
    setDownloading(true);
    const token = localStorage.getItem('authToken');
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${config.apiBaseUrl}/api/pdocs/download/${document.document_id}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'blob';

    xhr.onload = async function () {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const url = window.URL.createObjectURL(blob);
        const { file_name, file_type } = document;

        if (file_type === 'application/pdf') {
          setDocumentContent(
            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.6.347/build/pdf.worker.min.js`}>
              <Viewer fileUrl={url} />
            </Worker>
          );
        } else if (file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocumentContent(<div dangerouslySetInnerHTML={{ __html: result.value }} />);
        } else if (file_type.startsWith('image/')) {
          setDocumentContent(
            <ModalImage
              small={url}
              large={url}
              alt={file_name}
            />
          );
        } else if (file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          const arrayBuffer = await blob.arrayBuffer();
          const workbook = read(arrayBuffer, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = utils.sheet_to_html(sheet);
          setDocumentContent(<div dangerouslySetInnerHTML={{ __html: data }} />);
        } else {
          setDocumentContent(<Typography variant="body1">Unsupported file type</Typography>);
        }

        window.URL.revokeObjectURL(url);
        setSnackbarMessage('File downloaded and rendered successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        console.error('Failed to download file:', xhr.statusText);
        setSnackbarMessage('Failed to download file. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      setDownloading(false);
    };

    xhr.onerror = function () {
      console.error('Network error while downloading file.');
      setSnackbarMessage('Network error while downloading file.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDownloading(false);
    };

    xhr.send();
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') {
      return <PictureAsPdf color="error" />;
    } else if (fileType.startsWith('image/')) {
      return <Image color="primary" />;
    } else {
      return <DescriptionIcon color="action" />;
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (gridApi) {
      gridApi.setQuickFilter(event.target.value);
    }
  };

  const ActionsCellRenderer = (params) => {
    console.log('Rendering actions for:', params.data);
    return (
      <Box display="flex" alignItems="center">
        <Tooltip title="Download">
          <IconButton onClick={() => handleDownload(params.data.document_id, params.data.document_name)}>
            <GetApp color="primary" />
          </IconButton>
        </Tooltip>
        <Tooltip title="View Document">
          <IconButton onClick={() => handleClickOpen(params.data)}>
            <DescriptionIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton onClick={() => handleDelete(params.data.document_id)}>
            <Delete color="error" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const columnDefs = [
    { headerName: 'Department Name', field: 'department_id.departmentName', sortable: true, filter: true },
    { headerName: 'Document Name', field: 'document_name', sortable: true, filter: true },
    { headerName: 'Description', field: 'description', sortable: true, filter: true },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
    },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    sortable: true,
    resizable: true,
  };

  const onGridReady = (params) => {
    console.log('Grid is ready');
    setGridApi(params.api);
  };

  return (
    <Box mt={3}>
      <Typography variant="h5" gutterBottom>
        Project Documents
      </Typography>
      <Paper elevation={3} sx={{ padding: 2, height: '100%' }}>
        <Box p={2} mb={3} border={1} borderColor="grey.300" borderRadius={4}>
          <Typography variant="h6" gutterBottom>
            Upload Documents
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentId}
                  onChange={handleDepartmentChange}
                  label="Department"
                >
                  {departments.map((department) => (
                    <MenuItem key={department.departmentId} value={department.departmentId}>
                      {department.departmentName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Document Name"
                variant="outlined"
                fullWidth
                value={documentName}
                onChange={handleDocumentNameChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={description}
                onChange={handleDescriptionChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <input
                accept="*"
                style={{ display: 'none' }}
                id="upload-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="upload-file">
                <Button variant="contained" color="primary" component="span" startIcon={<CloudUpload />}>
                  Select File
                </Button>
              </label>
              {fileName && (
                <Typography variant="body2" style={{ marginTop: 10 }}>
                  Selected File: {fileName}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={<CloudUpload />}
              >
                Upload Document
              </Button>
              {uploading && <CircularProgress size={24} style={{ marginLeft: 15 }} />}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box mt={3}>
        <TextField
          label="Search Documents"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        {loading ? (
          <CircularProgress />
        ) : (
          <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%', marginTop: 2 }}>
            <AgGridReact
              columnDefs={columnDefs}
              rowData={documents}
              pagination={true}
              paginationPageSize={10}
              onGridReady={onGridReady}
              defaultColDef={defaultColDef}
              frameworkComponents={{
                actionsCellRenderer: ActionsCellRenderer,
              }}
            />
          </Box>
        )}
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Document Viewer</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Typography variant="h6">{selectedDocument.document_name}</Typography>
              <Typography variant="body1">{selectedDocument.description}</Typography>
              {documentContent}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <Button onClick={handleClose} color="primary">
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectDocuments;